addEventListener( 'DOMContentLoaded', function () {
	// ── Lightbox-navigator (images + HLS video), slides via Swiper ──────────
	let overlay = null;
	let swiperContainerEl = null;
	let swiperWrapperEl = null;
	let prevButton = null;
	let nextButton = null;
	let soundButton = null;
	let soundIcon = null;
	let swiperInstance = null;
	let activeHls = null;
	let activeVideoForSound = null;

	/**
	 * Collects the currently visible gallery items (images/videos) inside a
	 * gallery-grid instance, in document order. Items hidden by the filter
	 * tabs (`js-hidden` on their parent gallery-section) are excluded.
	 *
	 * @param {Element} grid The `.b-gallery-grid` element.
	 * @return {Array} List of { type, figure, src|hlsSrc, alt, poster, enableSoundControl }.
	 */
	function collectItems( grid ) {
		const figures = grid.querySelectorAll(
			'.b-gallery-section:not(.js-hidden) .b-gallery-section__image'
		);

		return Array.from( figures ).map( ( figure ) => {
			const videoWrapper = figure.querySelector(
				'.b-gallery-section__video-wrapper[data-hls-src]'
			);

			if ( videoWrapper ) {
				const video = videoWrapper.querySelector( 'video' );
				return {
					type: 'video',
					figure,
					hlsSrc: videoWrapper.getAttribute( 'data-hls-src' ),
					poster: video ? video.getAttribute( 'poster' ) || '' : '',
					enableSoundControl: videoWrapper.getAttribute( 'data-sound-control' ) === '1',
				};
			}

			const img = figure.querySelector( 'img' );
			return {
				type: 'image',
				figure,
				src: figure.dataset.full || ( img ? img.src : '' ),
				alt: img ? img.alt : '',
			};
		} );
	}

	/**
	 * Builds one `.swiper-slide` for a gallery item via DOM APIs (not
	 * innerHTML), so URLs/alt text from the media library can't break out of
	 * an attribute.
	 *
	 * @param {Object} item A single item from `collectItems()`.
	 * @return {Element} The slide element.
	 */
	function buildSlideElement( item ) {
		const slide = document.createElement( 'div' );
		slide.className = 'swiper-slide';

		if ( item.type === 'video' ) {
			slide.setAttribute( 'data-hls-src', item.hlsSrc );
			slide.setAttribute( 'data-sound-control', item.enableSoundControl ? '1' : '0' );

			const video = document.createElement( 'video' );
			video.className = 'c-lightbox__video';
			video.playsInline = true;
			video.controls = true;
			if ( item.poster ) video.poster = item.poster;
			slide.appendChild( video );
		} else {
			const img = document.createElement( 'img' );
			img.className = 'c-lightbox__img';
			img.src = item.src;
			img.alt = item.alt;
			slide.appendChild( img );
		}

		return slide;
	}

	function buildLightbox( arrowUrl, soundIconUrl, soundIconOffUrl ) {
		const el = document.createElement( 'div' );
		el.className = 'c-lightbox';
		el.setAttribute( 'aria-modal', 'true' );
		el.setAttribute( 'role', 'dialog' );
		el.setAttribute( 'aria-label', 'Lightbox' );
		el.innerHTML = `
			<button class="c-lightbox__close escape-btn-styles" aria-label="Cerrar">
				<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
					<line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
				</svg>
			</button>
			<button class="c-lightbox__nav c-lightbox__nav--prev escape-btn-styles" aria-label="Anterior">
				<img class="c-lightbox__nav-arrow" src="${ arrowUrl }" alt="" />
			</button>
			<div class="c-lightbox__content">
				<div class="c-lightbox__swiper swiper">
					<div class="swiper-wrapper"></div>
				</div>
				<button
					class="c-lightbox__sound-control escape-btn-styles is-muted"
					aria-pressed="false"
					aria-label="Activar sonido"
					data-label-muted="Activar sonido"
					data-label-unmuted="Silenciar"
					data-icon-muted="${ soundIconUrl }"
					data-icon-unmuted="${ soundIconOffUrl }"
				>
					<img class="c-lightbox__sound-control-icon" src="${ soundIconUrl }" alt="" />
				</button>
			</div>
			<button class="c-lightbox__nav c-lightbox__nav--next escape-btn-styles" aria-label="Siguiente">
				<img class="c-lightbox__nav-arrow" src="${ arrowUrl }" alt="" />
			</button>`;
		document.body.appendChild( el );

		swiperContainerEl = el.querySelector( '.c-lightbox__swiper' );
		swiperWrapperEl = el.querySelector( '.swiper-wrapper' );
		prevButton = el.querySelector( '.c-lightbox__nav--prev' );
		nextButton = el.querySelector( '.c-lightbox__nav--next' );
		soundButton = el.querySelector( '.c-lightbox__sound-control' );
		soundIcon = el.querySelector( '.c-lightbox__sound-control-icon' );
		soundButton.style.display = 'none';

		el.addEventListener( 'click', ( e ) => {
			// The swiper slide fills the whole 90vw×90vh box, but the image/video
			// itself may be letterboxed smaller inside it — only clicks on the
			// media or an actual control should stay open.
			const isOnMedia = e.target.closest( '.c-lightbox__img' ) || e.target.closest( '.c-lightbox__video' );
			const isOnControl =
				e.target.closest( '.c-lightbox__nav' ) ||
				e.target.closest( '.c-lightbox__close' ) ||
				e.target.closest( '.c-lightbox__sound-control' );

			if ( ! isOnMedia && ! isOnControl ) {
				closeLightbox();
			}
		} );

		el.querySelector( '.c-lightbox__close' ).addEventListener( 'click', closeLightbox );

		soundButton.addEventListener( 'click', () => {
			if ( ! activeVideoForSound ) return;
			activeVideoForSound.muted = ! activeVideoForSound.muted;
			if ( ! activeVideoForSound.muted ) {
				activeVideoForSound.play().catch( () => {} );
			}
			syncSoundUi();
		} );

		return el;
	}

	function syncSoundUi() {
		if ( ! soundButton || ! activeVideoForSound ) return;
		const isMuted = activeVideoForSound.muted;
		soundButton.classList.toggle( 'is-muted', isMuted );
		soundButton.classList.toggle( 'is-unmuted', ! isMuted );
		soundButton.setAttribute( 'aria-pressed', String( ! isMuted ) );
		const label = soundButton.getAttribute(
			isMuted ? 'data-label-muted' : 'data-label-unmuted'
		);
		soundButton.setAttribute( 'aria-label', label );
		if ( soundIcon ) {
			soundIcon.src = soundButton.getAttribute(
				isMuted ? 'data-icon-muted' : 'data-icon-unmuted'
			);
		}
	}

	/**
	 * Pins the sound-control button to the top-right corner of the actual
	 * rendered video box (not the swiper container, which is a fixed 90vw×90vh
	 * box the video is letterboxed inside via `object-fit: contain`).
	 */
	function updateSoundControlPosition() {
		if ( ! soundButton || ! activeVideoForSound || soundButton.style.display === 'none' ) return;
		const rect = activeVideoForSound.getBoundingClientRect();
		soundButton.style.top = `${ rect.top + 12 }px`;
		soundButton.style.left = `${ rect.right - soundButton.offsetWidth - 12 }px`;
	}

	function stopAllVideos( swiper ) {
		if ( activeHls ) {
			activeHls.destroy();
			activeHls = null;
		}
		Array.from( swiper.slides ).forEach( ( slide ) => {
			const video = slide.querySelector( 'video' );
			if ( ! video ) return;
			video.pause();
			video.removeAttribute( 'src' );
			video.load();
		} );
		activeVideoForSound = null;
	}

	/**
	 * Sets up the currently-active slide: shows/hides the sound-control
	 * button and, for a video slide, (re)starts its HLS playback muted.
	 *
	 * @param {Object} swiper The Swiper instance.
	 */
	function activateSlide( swiper ) {
		stopAllVideos( swiper );

		const slide = swiper.slides[ swiper.activeIndex ];
		if ( ! slide ) return;

		const video = slide.querySelector( 'video' );
		const enableSoundControl = slide.getAttribute( 'data-sound-control' ) === '1';

		if ( video && enableSoundControl ) {
			activeVideoForSound = video;
			soundButton.style.display = '';
			syncSoundUi();
			updateSoundControlPosition();
		} else {
			soundButton.style.display = 'none';
		}

		const hlsSrc = slide.getAttribute( 'data-hls-src' );
		if ( ! video || ! hlsSrc ) return;

		// Always start muted so autoplay is reliable across browsers; the
		// sound-control button (when enabled) lets the visitor opt in.
		video.muted = true;

		// Safari supports HLS natively.
		if ( video.canPlayType( 'application/vnd.apple.mpegurl' ) ) {
			video.src = hlsSrc;
			video.addEventListener(
				'loadedmetadata',
				() => {
					video.play().catch( () => {} );
					updateSoundControlPosition();
				},
				{ once: true }
			);
			return;
		}

		// Use hls.js for other browsers.
		if ( typeof Hls !== 'undefined' && Hls.isSupported() ) {
			activeHls = new Hls();
			activeHls.loadSource( hlsSrc );
			activeHls.attachMedia( video );
			activeHls.on( Hls.Events.MANIFEST_PARSED, () => {
				video.play().catch( () => {} );
				updateSoundControlPosition();
			} );
			activeHls.on( Hls.Events.ERROR, ( event, data ) => {
				if ( data.fatal ) {
					switch ( data.type ) {
						case Hls.ErrorTypes.NETWORK_ERROR:
							activeHls.startLoad();
							break;
						case Hls.ErrorTypes.MEDIA_ERROR:
							activeHls.recoverMediaError();
							break;
						default:
							activeHls.destroy();
					}
				}
			} );
		}
	}

	function destroySwiper() {
		if ( swiperInstance ) {
			swiperInstance.destroy( true, true );
			swiperInstance = null;
		}
	}

	function openLightbox( grid, items, index ) {
		if ( ! overlay ) {
			overlay = buildLightbox(
				grid.dataset.lightboxArrow || '',
				grid.dataset.lightboxSoundIcon || '',
				grid.dataset.lightboxSoundIconOff || ''
			);
		}

		destroySwiper();
		swiperWrapperEl.innerHTML = '';
		items.forEach( ( item ) => swiperWrapperEl.appendChild( buildSlideElement( item ) ) );

		const hasMultiple = items.length > 1;
		prevButton.style.display = hasMultiple ? '' : 'none';
		nextButton.style.display = hasMultiple ? '' : 'none';

		const swiperConfig = {
			slidesPerView: 1,
			spaceBetween: 16,
			initialSlide: index,
		};
		if ( hasMultiple ) {
			swiperConfig.navigation = { nextEl: nextButton, prevEl: prevButton };
		}

		swiperInstance = new Swiper( swiperContainerEl, swiperConfig );
		swiperInstance.on( 'slideChange', () => activateSlide( swiperInstance ) );
		activateSlide( swiperInstance );

		overlay.classList.add( 'js-open' );
		document.documentElement.classList.add( 'js-lightbox-open' );
	}

	function closeLightbox() {
		if ( ! overlay ) return;
		overlay.classList.remove( 'js-open' );
		document.documentElement.classList.remove( 'js-lightbox-open' );
		if ( activeHls ) {
			activeHls.destroy();
			activeHls = null;
		}
		destroySwiper();
		swiperWrapperEl.innerHTML = '';
		activeVideoForSound = null;
	}

	document.addEventListener( 'keydown', ( e ) => {
		if ( ! overlay || ! overlay.classList.contains( 'js-open' ) || ! swiperInstance ) return;
		if ( e.key === 'Escape' ) closeLightbox();
		if ( e.key === 'ArrowRight' ) swiperInstance.slideNext();
		if ( e.key === 'ArrowLeft' ) swiperInstance.slidePrev();
	} );

	addEventListener( 'resize', () => {
		if ( overlay && overlay.classList.contains( 'js-open' ) ) {
			updateSoundControlPosition();
		}
	} );

	document.querySelectorAll( '.b-gallery-grid' ).forEach( ( grid ) => {
		grid.addEventListener( 'click', ( e ) => {
			const figure = e.target.closest( '.b-gallery-section__image' );
			if ( ! figure ) return;

			const items = collectItems( grid );
			const index = items.findIndex( ( item ) => item.figure === figure );
			if ( index === -1 ) return;

			openLightbox( grid, items, index );
		} );
	} );
} );
